import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import type { EventPage } from '../../../services/event/fetchEventPage'
import type { ScheduleOrder } from '../filterEvents'
import {
  appendEventPage,
  hasMorePages,
  nextPageNumber,
  type EventStreamData,
} from '../eventStreamPages'
import { frontierOf, type StreamFrontier } from '../scheduleWatermark'
import type { SnEvent } from '../types'

export interface EventStream {
  /** Every loaded page, flattened, in the server's order. */
  events: SnEvent[]
  /** Where the loaded pages reach, and whether more remain. */
  frontier: StreamFrontier
  isLoading: boolean
  isError: boolean
  /** True while any page of this stream is in flight. */
  isFetching: boolean
  /** No-op while a page is already in flight or the stream is exhausted. */
  fetchNextPage: () => void
}

export interface UseEventStreamArgs {
  queryKey: unknown[]
  fetchPage: (page: number) => Promise<EventPage>
  enabled: boolean
  /** The display order the pages arrive in. Drives which end is the frontier. */
  order?: ScheduleOrder
}

/**
 * Events change often enough that a longer window shows stale rows after a
 * teammate adds one. Matches the fetch-all hooks this sits beside.
 */
const EVENT_LIST_STALE_TIME = 1000 * 15

/**
 * One paginated event list, as a stream the schedule merge can consume.
 *
 * The primitive under `useScheduleEventStream`, `useCompetitionEventStream` and
 * `useMembershipEventStream` — the paging rule lives here once so the three
 * cannot answer differently about where their pages end.
 *
 * The counterpart of the fetch-all hooks, which drain every page before
 * returning. Those stay for the callers that need a complete list; this one
 * exists because the schedule lists do not — they page as the user scrolls,
 * against the games list's own pagination.
 *
 * ## Why not `useInfiniteQuery`
 *
 * It is the obvious tool and it is NOT usable here. React Native bundles
 * `@tanstack/query-core` from TypeScript source (`"react-native": "src/index.ts"`),
 * and under Metro the import cycle between `index`, `queryObserver` and
 * `infiniteQueryObserver` leaves `InfiniteQueryObserver` without its base
 * class's methods at runtime — `getOptimisticResult` resolves, the inherited
 * `getCurrentResult` does not, and every screen using it throws on mount. The
 * published build resolves that cycle ahead of time; the source does not.
 *
 * Accumulating by hand costs a little more code and buys two things back:
 *
 * - **Revalidation is one request, not N.** An infinite query refetches EVERY
 *   cached page whenever it revalidates, so a list scrolled six pages deep cost
 *   six requests to re-enter. Here page one IS the query, so that is all a
 *   refetch fetches.
 * - **Invalidation resets rather than rebuilds.** When `useEventMutations`
 *   invalidates `events.list()`, this refetches into a fresh page one instead
 *   of re-downloading stale depth the user is not looking at.
 *
 * Pages accumulate INSIDE the cache entry rather than in component state, which
 * is what makes both of those fall out: there is one key, and whatever the
 * query function last returned is the whole list.
 *
 * Exposes NO `refetch`, and needs none: the window is part of the query key so a
 * period switch refetches on its own, and mutations invalidate the prefix these
 * keys nest under.
 */
export const useEventStream = ({
  queryKey,
  fetchPage,
  enabled,
  order = 'asc',
}: UseEventStreamArgs): EventStream => {
  'use memo'

  const queryClient = useQueryClient()
  const [isFetchingNext, setIsFetchingNext] = useState(false)

  const query = useQuery<EventStreamData>({
    queryKey,
    //  Page ONE only. Later pages are appended by `fetchNextPage` below, so a
    //  refetch of this key deliberately collapses the list back to its first
    //  page rather than replaying every page the user had scrolled through.
    queryFn: async () => ({ pages: [await fetchPage(1)] }),
    enabled,
    staleTime: EVENT_LIST_STALE_TIME,
    // Dropped as soon as nothing observes this period's stream. Without it,
    // leaving a period and returning after the stale window shows the
    // accumulated pages and then visibly collapses them to page one when the
    // refetch lands. Dropping the entry means re-entry is simply a clean page
    // one. What is lost is the scrolled depth — not visible on re-entry
    // anyway, since the list renders from the top.
    //
    // Safe because the hook stays mounted across a period change: only the key
    // moves, so the incoming period always has an observer and only the
    // outgoing one is collected. (React StrictMode's double-mount will show one
    // extra page-one fetch in dev; production mounts once.)
    gcTime: 0,
    // Deliberately does NOT override the app-wide `refetchOnWindowFocus: false`.
    // The reason the fetch-all hooks needed it is gone: they sent no date
    // params, so switching period re-filtered an already-fetched list and
    // returning to the tab was the only moment an outside edit could land. The
    // window is part of THIS query's key, so a period switch refetches on its
    // own. The games stream this merges with has never refetched on focus
    // either, so the two behave alike.
  })

  const pages = query.data?.pages
  const events = (pages ?? []).flatMap((page) => page?.data ?? [])

  //  Read inside the handler rather than closed over, so a `fetchNextPage`
  //  captured by a scroll listener several renders ago still appends to the
  //  CURRENT page set instead of resurrecting an old one.
  const isAppendingRef = useRef(false)

  const fetchNextPage = () => {
    if (isAppendingRef.current || query.isFetching) {
      return
    }
    const current = queryClient.getQueryData<EventStreamData>(queryKey)
    const next = nextPageNumber(current)
    if (next === null) {
      return
    }

    isAppendingRef.current = true
    setIsFetchingNext(true)
    fetchPage(next)
      .then((page) => {
        //  Re-read: a refetch or an invalidation may have replaced the entry
        //  while this request was in flight, and appending to the stale value
        //  would resurrect pages the reset had just dropped.
        const latest = queryClient.getQueryData<EventStreamData>(queryKey)
        if (nextPageNumber(latest) !== next) {
          return
        }
        queryClient.setQueryData<EventStreamData>(queryKey, (old) =>
          appendEventPage(old, page)
        )
      })
      //  Swallowed on purpose: the page simply does not append, `hasMore` stays
      //  true, and the next scroll retries. Surfacing it would blank a list the
      //  user is reading over one failed page.
      .catch(() => undefined)
      .finally(() => {
        isAppendingRef.current = false
        setIsFetchingNext(false)
      })
  }

  return {
    events,
    frontier: {
      lastStartsAt: frontierOf(events, order),
      hasMore: hasMorePages(pages),
      // Loading a list this stream does not hold yet — a first fetch, or a
      // refetch after the period changed. Reported so the watermark holds
      // everything back rather than reading an unloaded stream as complete.
      isPending: query.isLoading,
    },
    // A DISABLED query reports `isLoading: false` — it fetches nothing, so
    // react-query considers it settled. The caller is still waiting on whatever
    // gates it, so report loading rather than letting the list flash empty.
    isLoading: enabled ? query.isLoading : true,
    isError: query.isError,
    isFetching: query.isFetching || isFetchingNext,
    fetchNextPage,
  }
}
